/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { useEuiTheme } from '../../services';
import { euiHorizontalRuleStyles } from './horizontal_rule.styles';
export var SIZES = ['full', 'half', 'quarter'];
export var MARGINS = ['none', 'xs', 's', 'm', 'l', 'xl', 'xxl'];
var marginToClassNameMap = {
  none: null,
  xs: 'marginXSmall',
  s: 'marginSmall',
  m: 'marginMedium',
  l: 'marginLarge',
  xl: 'marginXLarge',
  xxl: 'marginXXLarge'
};
export var EuiHorizontalRule = ({
  className,
  size = 'full',
  margin = 'l',
  ...rest
}) => {
  const euiTheme = useEuiTheme();
  const styles = euiHorizontalRuleStyles(euiTheme);
  const classes = classNames('euiHorizontalRule', {
    [`euiHorizontalRule--${size}`]: size,
    [`euiHorizontalRule--${marginToClassNameMap[margin]}`]: margin && margin !== 'none'
  }, className);
  const cssStyles = [styles.euiHorizontalRule, styles[size], styles[margin]];
  return <hr css={cssStyles} className={classes} {...rest} />;
};
EuiHorizontalRule.propTypes = {
  /**
     * Defines the width of the HR.
     */
  size: PropTypes.any,
  margin: PropTypes.any,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  EuiHorizontalRule.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/horizontal_rule/horizontal_rule.tsx',
    description: '',
    displayName: 'EuiHorizontalRule',
    methods: [],
    props: {
      size: {
        defaultValue: {
          value: 'full'
        },
        description: 'Defines the width of the HR.',
        name: 'size',
        parent: {
          fileName: 'app/src/components/horizontal_rule/horizontal_rule.tsx',
          name: 'EuiHorizontalRuleProps'
        },
        declarations: [{
          fileName: 'app/src/components/horizontal_rule/horizontal_rule.tsx',
          name: 'EuiHorizontalRuleProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"full" | "half" | "quarter"',
          value: [{
            value: '"full"'
          }, {
            value: '"half"'
          }, {
            value: '"quarter"'
          }]
        }
      },
      margin: {
        defaultValue: {
          value: 'l'
        },
        description: '',
        name: 'margin',
        parent: {
          fileName: 'app/src/components/horizontal_rule/horizontal_rule.tsx',
          name: 'EuiHorizontalRuleProps'
        },
        declarations: [{
          fileName: 'app/src/components/horizontal_rule/horizontal_rule.tsx',
          name: 'EuiHorizontalRuleProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"xs" | "s" | "m" | "l" | "xl" | "none" | "xxl"',
          value: [{
            value: '"xs"'
          }, {
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }, {
            value: '"none"'
          }, {
            value: '"xxl"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['EuiHorizontalRuleProps', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}